import { Component } from '@theme/component';
import { CartAddEvent, CartErrorEvent } from '@theme/events';

/**
 * Shop the scene quick-layout modal.
 *
 * @typedef {Object} ShopTheSceneModalRefs
 * @property {import('./dialog').DialogComponent} dialogComponent
 * @property {HTMLButtonElement} closeButton
 * @property {HTMLElement} productsGrid
 * @property {HTMLButtonElement} bulkAddButton
 * @property {HTMLElement[]} productCards
 * @property {HTMLButtonElement[]} productAddButtons
 */

/**
 * @extends {Component<ShopTheSceneModalRefs>}
 */
class ShopTheSceneModal extends Component {
  /** Only direct-child refs resolve here; controls inside dialog-component are queried separately. */
  requiredRefs = ['dialogComponent'];

  connectedCallback() {
    super.connectedCallback();
    this.#bindProductCards();
    this.#getBulkAddButton()?.addEventListener('click', this.#handleBulkAdd);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#getBulkAddButton()?.removeEventListener('click', this.#handleBulkAdd);
    this.#unbindProductCards();
  }

  open() {
    const dialogComponent = this.#getDialogComponent();
    if (dialogComponent?.showDialog) {
      dialogComponent.showDialog();
    }
  }

  close() {
    const dialogComponent = this.#getDialogComponent();
    if (dialogComponent?.closeDialog) {
      dialogComponent.closeDialog();
    }
  }

  /**
   * @returns {import('./dialog').DialogComponent | null}
   */
  #getDialogComponent() {
    const { dialogComponent } = this.refs;
    if (dialogComponent && 'showDialog' in dialogComponent) {
      return /** @type {import('./dialog').DialogComponent} */ (dialogComponent);
    }

    const fallback = this.querySelector('dialog-component');
    if (fallback && 'showDialog' in fallback) {
      return /** @type {import('./dialog').DialogComponent} */ (fallback);
    }

    return null;
  }

  /**
   * @returns {HTMLButtonElement | null}
   */
  #getBulkAddButton() {
    const button = this.querySelector('.shop-the-scene-modal__footer-add');
    return button instanceof HTMLButtonElement ? button : null;
  }

  /**
   * @returns {HTMLElement | null}
   */
  #getProductsGrid() {
    const grid = this.querySelector('.shop-the-scene-modal__products');
    return grid instanceof HTMLElement ? grid : null;
  }

  #bindProductCards() {
    const cards = this.#getProductCards();

    for (const card of cards) {
      card.addEventListener('click', this.#handleCardClick);
    }
  }

  #unbindProductCards() {
    const cards = this.#getProductCards();

    for (const card of cards) {
      card.removeEventListener('click', this.#handleCardClick);
    }
  }

  /**
   * @returns {HTMLElement[]}
   */
  #getProductCards() {
    return Array.from(this.#getProductsGrid()?.querySelectorAll('.shop-the-scene-modal__product-card') ?? []);
  }

  /**
   * @param {Event} event
   */
  #handleCardClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const pill = target.closest('.product-card-default__variant-pill');
    if (pill instanceof HTMLButtonElement) {
      event.preventDefault();
      this.#selectVariant(pill);
      return;
    }

    const addButton = target.closest('.shop-the-scene-modal__product-card-add');
    if (addButton instanceof HTMLButtonElement) {
      event.preventDefault();
      this.#addSingleProduct(addButton);
    }
  };

  /**
   * @param {HTMLButtonElement} pill
   */
  #selectVariant(pill) {
    const card = pill.closest('.shop-the-scene-modal__product-card');
    if (!(card instanceof HTMLElement)) return;

    const variantId = pill.dataset.variantId;
    if (!variantId) return;

    const pills = card.querySelectorAll('.product-card-default__variant-pill');
    for (const item of pills) {
      if (!(item instanceof HTMLButtonElement)) continue;
      const isActive = item === pill;
      item.classList.toggle('is-active', isActive);
      item.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    }

    const addButton = card.querySelector('.shop-the-scene-modal__product-card-add');
    if (addButton instanceof HTMLButtonElement) {
      addButton.dataset.variantId = variantId;
      addButton.disabled = pill.dataset.variantAvailable === 'false';
    }
  }

  /**
   * @param {HTMLButtonElement} button
   */
  async #addSingleProduct(button) {
    const variantId = button.dataset.variantId;
    if (!variantId || button.disabled) return;

    await this.#addItemsToCart([{ variantId, quantity: 1 }], button);
  }

  /**
   * @param {Event} event
   */
  #handleBulkAdd = async (event) => {
    event.preventDefault();

    const items = this.#getSelectedItems();
    if (items.length === 0) return;

    const bulkAddButton = this.#getBulkAddButton();
    if (!bulkAddButton) return;

    await this.#addItemsToCart(items, bulkAddButton);
  };

  /**
   * @returns {Array<{variantId: string, quantity: number}>}
   */
  #getSelectedItems() {
    /** @type {Array<{variantId: string, quantity: number}>} */
    const items = [];

    for (const card of this.#getProductCards()) {
      const addButton = card.querySelector('.shop-the-scene-modal__product-card-add');
      const hiddenVariant = card.querySelector('[data-variant-id]');

      let variantId = '';
      if (addButton instanceof HTMLButtonElement && addButton.dataset.variantId) {
        variantId = addButton.dataset.variantId;
      } else if (hiddenVariant instanceof HTMLElement && hiddenVariant.dataset.variantId) {
        variantId = hiddenVariant.dataset.variantId;
      }

      if (!variantId) continue;

      const isDisabled = addButton instanceof HTMLButtonElement && addButton.disabled;
      if (isDisabled) continue;

      items.push({ variantId, quantity: 1 });
    }

    return items;
  }

  /**
   * @param {Array<{variantId: string, quantity: number}>} items
   * @param {HTMLButtonElement} trigger
   */
  async #addItemsToCart(items, trigger) {
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionIds = [];

    for (const item of cartItemsComponents) {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionIds.push(item.dataset.sectionId);
      }
    }

    const payload = {
      items: items.map((item) => ({
        id: Number(item.variantId),
        quantity: item.quantity,
      })),
      sections: sectionIds.join(','),
    };

    trigger.disabled = true;

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (data.status) {
        document.dispatchEvent(
          new CartErrorEvent(this.id, data.message, data.description, data.errors)
        );
        return;
      }

      const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
      document.dispatchEvent(
        new CartAddEvent(data, this.id, {
          source: 'shop-the-scene-modal',
          itemCount: totalQuantity,
          sections: data.sections,
        })
      );

      this.close();
    } catch (error) {
      console.error(error);
    } finally {
      trigger.disabled = false;
    }
  }
}

if (!customElements.get('shop-the-scene-modal')) {
  customElements.define('shop-the-scene-modal', ShopTheSceneModal);
}
